import { useMemo } from 'react'
import { useGeofenceActivity } from '@/hooks/useGeofenceActivity'
import { getDayRange, pbDate } from '@/lib/dates'
import { useMonitorMoment, useMonitorStore } from '@/stores/monitor-store'
import type { UserLocationWithStatus } from '@/types/movement'
import { defaultTrackingStatus } from './defaultTrackingStatus'
import { useMonitorBase, useMonitorStatuses } from './useMonitorBase'
import { useUsersLocationAuth } from './useUsersLocationAuth'

export function useLiveMonitor() {
    const base = useMonitorBase()
    const userVisibility = useMonitorStore(state => state.userVisibility)
    const selectedLocationId = useMonitorStore(state => state.selectedLocationId)
    const dateRange = useMonitorStore(state => state.dateRange)
    const { selectedMoment } = useMonitorMoment()

    const { statusMap, isPending: isLoadingStatus } = useMonitorStatuses(base)
    const locationAuthMap = useUsersLocationAuth(base.orgId, base.orgUserIds)

    // Match useMonitorBase's query window: when a snapshot moment is set, use
    // its calendar day; otherwise fall back to the stored dateRange. Without
    // this, geofence-activity could query a different day than movement during
    // the moment between picking a snapshot and the dateRange catching up.
    const queryDayBase = selectedMoment ?? dateRange.startDate
    const fullDayRange = useMemo(() => getDayRange(queryDayBase), [queryDayBase])
    const startDateStr = pbDate(fullDayRange.startDate)
    const endDateStr = pbDate(fullDayRange.endDate)

    const { selectedLocationActivity, isPending: isGeofenceDataPending } = useGeofenceActivity({
        orgUserIds: base.orgUserIds,
        startDate: startDateStr,
        endDate: endDateStr,
        selectedLocationId,
    })

    const selectedLocation = useMemo(() => {
        if (!selectedLocationId) return null
        return base.locationCircles.find(c => c.id === selectedLocationId) ?? null
    }, [selectedLocationId, base.locationCircles])

    const userLocations = useMemo<UserLocationWithStatus[]>(() => {
        const byId = new Map(base.rawUserLocations.map(u => [u.userId, u]))

        return base.orgUserIds.map(id => {
            const existing = byId.get(id)
            const trackingStatus = statusMap.get(id) ?? defaultTrackingStatus(id)
            const visible = userVisibility[id] ?? true
            const dayEntry = base.dayEntries.get(id)
            const locationAuth = locationAuthMap.get(id)
            if (existing) {
                return { ...existing, visible, trackingStatus, dayEntry, locationAuth }
            }
            const storedColor = base.userColorMap.get(id)
            return {
                userId: id,
                userName: base.fetchedUsers.get(id)?.name ?? 'Unknown User',
                path: [],
                mostRecentLocation: undefined,
                color: storedColor || '#888888',
                visible,
                trackingStatus,
                dayEntry,
                locationAuth,
            }
        })
    }, [
        base.rawUserLocations,
        base.orgUserIds,
        base.fetchedUsers,
        base.userColorMap,
        base.dayEntries,
        userVisibility,
        statusMap,
        locationAuthMap,
    ])

    return {
        userLocations,
        userColorMap: base.userColorMap,
        locationCircles: base.locationCircles,
        selectedLocation,
        selectedLocationActivity,
        isGeofenceDataPending,
        isLoadingMovements: base.isLoadingMovements,
        isPending: base.isPending || isLoadingStatus,
        now: base.now,
    }
}
